import { useGlobalConfig } from '@/components/GlobalContext';
import { CardItem } from '@/components/Rotator/Rotator';

import { AppResolution, AppResolutionType } from '@/enums';
import {
  Badge,
  ComputedFlag,
  ContentMetadata,
  FlagType,
  Image,
  Maybe,
  MediaMetadata,
} from '@/services/graphQL/api/generated/types_web';
import { ContainerType } from '@/services/graphQL/hooks/useScreenQuery';
import { ContainerDisplayStyle } from '@/utils/containerDisplayStyle';

import { getLocalization, stringFromFormat } from './localization';

// Scale factor for 1080p → 720p resolution
const resolutionScaleFactor720 = 0.6667;

export type ImageDimensionOptions = {
  width?: number;
  height?: number;
  maintain_aspect?: boolean;
  bfc?: 'off';
};

export function getGenreInfo(media: MediaMetadata | undefined): string {
  if (!media?.genres?.length) return '';
  return media.genres
    .map((genre) => genre?.displayName)
    .filter(Boolean)
    .join(' • ');
}

function pluralizeSeasonCount(seasonCount: number): string {
  const { userLanguage } = useGlobalConfig();

  if (seasonCount === 1) {
    return getLocalization(userLanguage)['more-info-screen']['season-singular'];
  } else {
    return stringFromFormat(
      getLocalization(userLanguage)['more-info-screen']['season-plural'],
      seasonCount
    );
  }
}

// Helper function to generate media year and session info
export function getYearSeasonInfo(productionYear: string, seasonCount: number): string {
  if (!seasonCount) return '';

  if (seasonCount && seasonCount > 0) {
    return `${productionYear} • ${pluralizeSeasonCount(seasonCount)}`;
  } else {
    return `${productionYear}`;
  }
}

// Helper function to get image url with resize parameters appended
export function getResizedImageURL(image: Image | undefined, width: number): string {
  if (!image) return '';
  return `${image.url}?width=${width}&maintain_aspect=1`;
}

/**
 * Get the scale factor for a given resolution.
 * At 720p, dimensions should be scaled to 0.6667 of their 1080p baseline.
 * At 1080p or undefined, the factor is 1.0 (no scaling).
 */
export function getResolutionScaleFactor(resolution?: AppResolutionType): number {
  return resolution === AppResolution.Hd ? resolutionScaleFactor720 : 1.0;
}

/**
 * Scale a dimension (width or height) based on the target resolution.
 * Takes a 1080p baseline dimension and returns the scaled value for the given resolution.
 * @param baseDimension The baseline dimension at 1080p
 * @param resolution The target resolution ('720', '1080', or undefined)
 * @returns The scaled dimension, rounded to nearest integer
 */
export function scaleDimensionForResolution(
  baseDimension: number,
  resolution?: AppResolutionType
): number {
  const scaleFactor = getResolutionScaleFactor(resolution);
  return Math.round(baseDimension * scaleFactor);
}

/**
 * Build a complete image URL with dimension parameters, scaled for the target resolution.
 * @param imageUrl The base image URL
 * @param options The dimension options (width or height)
 * @param resolution The target resolution ('720', '1080', or undefined)
 * @returns The complete image URL with scaled dimension parameter
 */
export function buildImageUrl(
  imageUrl: string | undefined,
  options: ImageDimensionOptions,
  resolution?: AppResolutionType
): string {
  if (!imageUrl) return '';

  const maintain_aspect = options.maintain_aspect ?? true;

  const params = new URLSearchParams();

  if (options.width !== undefined) {
    params.set('width', String(scaleDimensionForResolution(options.width, resolution)));
  }

  if (options.height !== undefined) {
    params.set('height', String(scaleDimensionForResolution(options.height, resolution)));
  }

  params.set('maintain_aspect', maintain_aspect ? '1' : '0');

  if (options.bfc !== undefined) {
    params.set('bfc', options.bfc);
  }

  const separator = imageUrl.includes('?') ? '&' : '?';

  return `${imageUrl}${separator}${params.toString()}`;
}

export function getMediaAdsFlagLabel(
  flags: ComputedFlag[] | undefined
): string | undefined {
  return flags?.find((flag) => flag?.flag === FlagType.Advertisement)?.label || undefined;
}

export function getMediaFreeFlagLabel(
  data: MediaMetadata | ContentMetadata | undefined
): string | undefined {
  return data?.flags?.find((flag) => flag?.flag === FlagType.Free)?.label || undefined;
}

// returns badge array with expiring and upcoming flags
// limits to 2 badges
// exists for business case where we want to display Upcoming and Expiring flags as badges
export function getBadgesWithFlags(
  badges?: Badge[] | null,
  flags?: ComputedFlag[] | null,
  badgesCount: number = 2
) {
  if (!badges || !flags) return [];
  const expiringFlags = flags
    .filter(
      (flag): flag is ComputedFlag => flag?.flag === FlagType.Expiring && flag !== null
    )
    .map((flag) => convertFlagToBadge(flag));
  const upcomingFlags = flags
    .filter(
      (flag): flag is ComputedFlag => flag?.flag === FlagType.Upcoming && flag !== null
    )
    .map((flag) => convertFlagToBadge(flag));

  return [...expiringFlags, ...upcomingFlags, ...(badges || [])].splice(0, badgesCount);
}

export function convertFlagToBadge(flag: ComputedFlag) {
  return {
    label: flag?.label,
    detailedLabel: flag?.detailedLabel || '',
  };
}

// Generic function to unwrap Maybe types and filter out null/undefined values
export function unwrapMaybeArray<T>(
  input: Maybe<Maybe<T>[]> | undefined
): T[] | undefined {
  const arrayContainer = input ?? undefined; // Handle outer Maybe

  if (!arrayContainer) {
    return undefined;
  }

  const items = arrayContainer.flatMap((maybeItem) => {
    const item = maybeItem ?? undefined; // Unwrap inner Maybe
    return item ? [item] : [];
  });

  return items.length > 0 ? items : undefined;
}

// returns true if the item's ContainerType should display a ShowPreview
export function cardItemHasShowPreview(item?: CardItem) {
  return !(
    item?.__typename === ContainerType.LinkImage ||
    item?.__typename === ContainerType.LinkText ||
    item?.featuredBlock ||
    item?.cardDisplayType === ContainerDisplayStyle.PROMO
  );
}
